import { getBackRoyalApiBaseConfig } from 'BackRoyalApi';
import { createApi } from 'ReduxHelpers';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { transformResponseWithCurrentUserPushMessages } from 'BackRoyalApi/transformResponseWithCurrentUserPushMessages';
import {
    type ChangePrefProgramParams,
    type EnsureAndChangePrefProgramParams,
    type OfferAdmissionAlumniQuanticLaunchParams,
    type SetActivePlaylistParams,
    type UpdateCurrentUserParams,
    type UserActionsResponse,
} from './Users.types';

const EMPTY_RESPONSE: UserActionsResponse = {
    meta: { pushMessages: { currentUser: {} } },
};

const JSON_HEADERS = { 'Content-Type': 'application/json' };

type RawResponse = {
    data?: UserActionsResponse;
    error?: FetchBaseQueryError;
};

function handlePushMessages(response: RawResponse): { data: UserActionsResponse } | { error: FetchBaseQueryError } {
    if (response.error) return { error: response.error };

    if (response.data?.meta) {
        const { pushMessages } = response.data.meta;
        if (pushMessages) transformResponseWithCurrentUserPushMessages(pushMessages);
        return { data: response.data };
    }

    return { data: EMPTY_RESPONSE };
}

export const userActionsApi = createApi('UserActions', {
    ...getBackRoyalApiBaseConfig('UserActions'),
    // oxlint-disable-next-line max-lines-per-function
    endpoints: builder => ({
        setActivePlaylist: builder.mutation<UserActionsResponse, SetActivePlaylistParams>({
            async queryFn({ program_type, playlist_locale_pack_id }, _api, _options, fetchWithBaseQuery) {
                const response = (await fetchWithBaseQuery({
                    url: 'user_actions/user/set_active_playlist.json',
                    method: 'POST',
                    body: { program_type, playlist_locale_pack_id },
                    headers: JSON_HEADERS,
                })) as RawResponse;

                return handlePushMessages(response);
            },
        }),

        changePrefProgram: builder.mutation<UserActionsResponse, ChangePrefProgramParams>({
            async queryFn({ user_program_field_state_id }, _api, _options, fetchWithBaseQuery) {
                const response = (await fetchWithBaseQuery({
                    url: 'user_actions/user/change_pref_program.json',
                    method: 'POST',
                    body: { user_program_field_state_id },
                    headers: JSON_HEADERS,
                })) as RawResponse;

                return handlePushMessages(response);
            },
        }),

        ensureAndChangePrefProgram: builder.mutation<UserActionsResponse, EnsureAndChangePrefProgramParams>({
            async queryFn({ program_type }, _api, _options, fetchWithBaseQuery) {
                const response = (await fetchWithBaseQuery({
                    url: 'user_actions/user/ensure_and_change_pref_program.json',
                    method: 'POST',
                    body: { program_type },
                    headers: JSON_HEADERS,
                })) as RawResponse;

                return handlePushMessages(response);
            },
        }),

        offerAdmissionAlumniQuanticLaunch: builder.mutation<
            UserActionsResponse,
            OfferAdmissionAlumniQuanticLaunchParams
        >({
            async queryFn({ program_type }, _api, _options, fetchWithBaseQuery) {
                const response = (await fetchWithBaseQuery({
                    url: 'user_actions/user/offer_admission_alumni_quantic_launch.json',
                    method: 'POST',
                    body: { program_type },
                    headers: JSON_HEADERS,
                })) as RawResponse;

                return handlePushMessages(response);
            },
        }),

        // userId is only used for client-side IndexedDB cleanup via frontRoyalStoreApi;
        // the server identifies the user from the session.
        clearAllProgress: builder.mutation<UserActionsResponse, { userId: string }>({
            async queryFn({ userId }, _api, _options, fetchWithBaseQuery) {
                const response = (await fetchWithBaseQuery({
                    url: 'destroy_all_progress.json',
                    method: 'DELETE',
                })) as RawResponse;

                if (response.error) return { error: response.error };

                // Lazy import to avoid circular dependency issues in test environments
                const { frontRoyalStoreApi } = await import('FrontRoyalStore');
                await frontRoyalStoreApi.makeRequest('clearProgressForUser', userId);

                return handlePushMessages(response);
            },
        }),

        updateCurrentUser: builder.mutation<UserActionsResponse, UpdateCurrentUserParams>({
            async queryFn({ user }, _api, _options, fetchWithBaseQuery) {
                const response = (await fetchWithBaseQuery({
                    url: 'users.json',
                    method: 'PUT',
                    body: { record: user },
                    headers: JSON_HEADERS,
                })) as RawResponse;

                return handlePushMessages(response);
            },
        }),
    }),
});
